import React, { useEffect, useState } from 'react';
import { Download, Mail, Eye, X } from 'lucide-react';
import { useToast } from '../../components/ui/ToastProvider';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Dialog } from '@headlessui/react';
import { auth, db } from '../../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { H1 } from '../../components/typography';
import { ownerDisplay } from '../../utils/applicationOwner';

const STATUS_COLORS = {
  PENDING: 'text-yellow-600',
  APPROVED: 'text-green-600',
  ACTIVE: 'text-blue-600',
  REJECTED: 'text-red-600',
};

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const color = STATUS_COLORS[status] || 'text-zinc-600';
  return (
    <span className={`text-xs font-medium ${color}`}>
      {t(`dashboard.export.status.${status?.toLowerCase()}`) || status}
    </span>
  );
}

function TimeTypeBadge({ rental, categorizeByTime }) {
  const timeType = categorizeByTime(rental);
  const typeLabels = {
    ongoing: { label: 'Pågående', color: 'text-green-600' },
    future: { label: 'Framtida', color: 'text-blue-600' },
    past: { label: 'Avslutad', color: 'text-gray-500' },
    unknown: { label: 'Okänd', color: 'text-gray-400' }
  };
  
  const { label, color } = typeLabels[timeType] || typeLabels.unknown;
  
  return (
    <span className={`text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

export default function ExportImport() {
  const { t } = useTranslation();
  const toast = useToast();
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeFilter, setTimeFilter] = useState('all');
  const [selectedRental, setSelectedRental] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchRentalsForUser(user.uid);
      } else {
        setError(t('dashboard.export.messages.noUser'));
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  async function fetchRentalsForUser(uid) {
    setLoading(true);
    setError(null);
    try {
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) throw new Error(t('dashboard.export.messages.noUserInfo'));
      const associationId = userSnap.data().associationId;
      if (!associationId) throw new Error(t('dashboard.export.messages.noBRF'));
      const q = query(
        collection(db, 'applications'),
        where('associationId', '==', associationId),
        where('status', 'in', ['APPROVED', 'ACTIVE', 'PENDING'])
      );
      const querySnapshot = await getDocs(q);
      const apps = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          apartmentNumber: data.apartmentNumber,
          objektsnummer: data.objektsnummer || '42-2025-1-1-9', // Fallback objektsnummer
          owner: data.brfEmail || '',
          // Manuellt inlagda uthyrningar har för- och efternamn men inget ownerName
          ownerName: ownerDisplay({ application: data, user: null }).name,
          period: `${data.fromDate || ''} – ${data.toDate || ''}`,
          fromDate: data.fromDate,
          toDate: data.toDate,
          status: data.status,
          reason: data.reason || '',
          tenant: `${data.tenantFirstName || ''} ${data.tenantLastName || ''}`.trim(),
          tenantEmail: data.tenantEmail || '',
          tenantPhoneNumber: data.tenantPhoneNumber || '',
        };
      });
      setRentals(apps);
    } catch (err) {
      setError(err.message || t('dashboard.export.messages.fetchError'));
    } finally {
      setLoading(false);
    }
  }

  const timeOptions = [
    { value: 'all', label: 'Alla typer' },
    { value: 'ongoing', label: 'Pågående' },
    { value: 'future', label: 'Framtida' },
    { value: 'past', label: 'Avslutade' }
  ];

  // Funktion för att kategorisera uthyrningar baserat på datum
  const categorizeByTime = (rental) => {
    if (!rental.fromDate || !rental.toDate) return 'unknown';
    
    const today = new Date();
    const fromDate = new Date(rental.fromDate);
    const toDate = new Date(rental.toDate);
    
    if (fromDate <= today && today <= toDate) {
      return 'ongoing'; // Pågående
    } else if (fromDate > today) {
      return 'future'; // Framtida
    } else if (toDate < today) {
      return 'past'; // Avslutade
    }
    return 'unknown';
  };

  // Filtrera baserat på tid
  const filteredRentals = timeFilter === 'all'
    ? rentals
    : rentals.filter(r => categorizeByTime(r) === timeFilter);

  const handleExport = () => {
    const doc = new jsPDF();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(t('dashboard.export.title') + ' – ' + t('dashboard.export.status.pending') + ', ' + t('dashboard.export.status.active') + ', ' + t('dashboard.export.status.approved'), 14, 18);
    doc.setFontSize(10);
    doc.text(`${t('dashboard.export.table.generated')}: ${new Date().toLocaleDateString('sv-SE')}`, 14, 26);
    doc.autoTable({
      startY: 32,
      head: [[
        t('dashboard.export.table.apartment'),
        'Objektsnummer',
        'Bostadsrättsägare',
        t('dashboard.export.table.tenant'),
        t('dashboard.export.table.period'),
        t('dashboard.export.table.status'),
        t('dashboard.export.table.contact')
      ]],
      body: filteredRentals.map(r => [
        r.apartmentNumber,
        r.objektsnummer,
        r.ownerName,
        r.tenant,
        r.period,
        t(`dashboard.export.status.${r.status?.toLowerCase()}`) || r.status,
        `${r.tenantEmail}${r.tenantPhoneNumber ? ' / ' + r.tenantPhoneNumber : ''}`
      ]),
      theme: 'grid',
      headStyles: { fillColor: [98, 184, 233] },
      styles: { font: 'helvetica', fontSize: 8 }, // Minska fontstorlek för att få plats med fler kolumner
      columnStyles: {
        1: { cellWidth: 25 }, // Objektsnummer
        4: { cellWidth: 30 }  // Period
      }
    });
    doc.save('uthyrningar.pdf');
    toast({ type: 'success', message: t('dashboard.export.messages.pdfCreated') });
  };

  const handleIndividualExport = (rental) => {
    const doc = new jsPDF();
    
    // Header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('Uthyrningsdetaljer', 14, 20);
    
    // Datum
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`${t('dashboard.export.table.generated')}: ${new Date().toLocaleDateString('sv-SE')}`, 14, 30);
    
    // Detaljinformation
    doc.setFontSize(12);
    let yPos = 45;
    
    const details = [
      ['Lägenhetsnummer:', rental.apartmentNumber],
      ['Objektsnummer:', rental.objektsnummer],
      ['Bostadsrättsägare:', rental.ownerName],
      ['Hyresgäst:', rental.tenant],
      ['Period:', rental.period],
      ['Status:', t(`dashboard.export.status.${rental.status?.toLowerCase()}`) || rental.status],
      ['Anledning:', rental.reason],
      ['E-post:', rental.tenantEmail],
      ['Telefon:', rental.tenantPhoneNumber || 'Ej angiven']
    ];
    
    details.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 14, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(value || 'Ej angiven', 70, yPos);
      yPos += 10;
    });
    
    // Footer
    doc.setFontSize(8);
    doc.text('Detta dokument är genererat från Hyria systemet', 14, 280);
    
    doc.save(`uthyrning_${rental.apartmentNumber}_${rental.tenant.replace(/\s+/g, '_')}.pdf`);
    toast({ type: 'success', message: `PDF skapad för lägenhet ${rental.apartmentNumber}!` });
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8">
      <H1>Export</H1>
      
      {/* Top bar med filter och export-knapp - Responsiv */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <label htmlFor="time" className="text-base font-medium text-zinc-700">Typ:</label>
          <select
            id="time"
            className="border border-zinc-200 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#62B8E9] w-full sm:min-w-[200px] sm:w-auto"
            value={timeFilter}
            onChange={e => setTimeFilter(e.target.value)}
          >
            {timeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <button
          className="flex items-center justify-center gap-3 bg-[#62B8E9] hover:bg-[#5ba3d4] text-white font-semibold px-6 py-3 rounded-xl shadow-lg transition text-base w-full sm:w-auto"
          onClick={handleExport}
          disabled={loading || filteredRentals.length === 0}
        >
          <Download className="w-5 h-5" /> 
          <span className="hidden sm:inline">{t('dashboard.export.table.exportToPDF')}</span>
          <span className="sm:hidden">Exportera</span>
        </button>
      </div>

      {loading ? (
        <div className="space-y-6">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="rounded-2xl border bg-white shadow p-6 animate-pulse h-40" />
          ))}
        </div>
      ) : error ? (
        <div className="text-red-500 py-8 text-center text-lg">{error}</div>
      ) : filteredRentals.length === 0 ? (
        <div className="text-zinc-500 py-8 text-center text-lg">{t('dashboard.export.messages.noRentals')}</div>
      ) : (
        <>
                    {/* Kompakt tabell för alla skärmar */}
          <div className="rounded-2xl shadow-lg border border-[#e6f3fa] bg-white max-h-[70vh] overflow-auto">
            <table className="w-full divide-y divide-[#e6f3fa]">
              <thead className="bg-[#f8fbfd] sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-zinc-700 text-sm">{t('dashboard.export.table.apartment')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-zinc-700 text-sm">{t('dashboard.export.table.tenant')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-zinc-700 text-sm">{t('dashboard.export.table.period')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-zinc-700 text-sm">{t('dashboard.export.table.status')}</th>
                  <th className="px-4 py-3 text-center font-semibold text-zinc-700 text-sm">Åtgärder</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e6f3fa]">
                {filteredRentals.map((r, idx) => (
                  <tr key={idx} className={`transition shadow-sm ${idx % 2 === 0 ? 'bg-white' : 'bg-[#f8fbfd]'} hover:bg-[#e6f3fa]`}>
                    <td className="px-4 py-3 text-zinc-800 text-sm font-medium">{r.apartmentNumber}</td>
                    <td className="px-4 py-3 text-zinc-800 text-sm font-medium">{r.tenant}</td>
                    <td className="px-4 py-3 text-zinc-600 text-sm whitespace-nowrap">{r.period}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button 
                        className="flex items-center gap-2 text-[#62B8E9] border border-[#62B8E9] hover:bg-[#62B8E9] hover:text-white transition px-3 py-2 rounded-lg text-sm font-medium"
                        onClick={() => setSelectedRental(r)}
                      >
                        <Eye className="w-4 h-4" />
                        <span className="hidden sm:inline">Visa mer</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Detaljmodal */}
      <Dialog open={!!selectedRental} onClose={() => setSelectedRental(null)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title className="text-xl font-bold text-gray-900">
                Uthyrningsdetaljer - Lägenhet {selectedRental?.apartmentNumber}
              </Dialog.Title>
              <button
                onClick={() => setSelectedRental(null)}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {selectedRental && (
              <div className="space-y-6">
                {/* Grundläggande information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Lägenhetsnummer</label>
                      <p className="text-sm text-gray-900">{selectedRental.apartmentNumber}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Objektsnummer</label>
                      <p className="text-sm text-gray-900">{selectedRental.objektsnummer}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Bostadsrättsägare</label>
                      <p className="text-sm text-gray-900">{selectedRental.ownerName}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Hyresgäst</label>
                      <p className="text-sm text-gray-900">{selectedRental.tenant}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Period</label>
                      <p className="text-sm text-gray-900">{selectedRental.period}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Typ</label>
                      <TimeTypeBadge rental={selectedRental} categorizeByTime={categorizeByTime} />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Status</label>
                      <div className="mt-1">
                        <StatusBadge status={selectedRental.status} />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Anledning</label>
                      <p className="text-sm text-gray-900">{selectedRental.reason || 'Ej angiven'}</p>
                    </div>
                  </div>
                </div>

                {/* Kontaktinformation */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Kontaktinformation</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">E-post</label>
                      <p className="text-sm text-gray-900">{selectedRental.tenantEmail || 'Ej angiven'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Telefon</label>
                      <p className="text-sm text-gray-900">{selectedRental.tenantPhoneNumber || 'Ej angiven'}</p>
                    </div>
                  </div>
                </div>

                {/* Export-knapp */}
                <div className="border-t pt-4">
                  <button
                    onClick={() => {
                      handleIndividualExport(selectedRental);
                      setSelectedRental(null);
                    }}
                    className="w-full flex items-center justify-center gap-3 bg-[#62B8E9] hover:bg-[#5ba3d4] text-white font-semibold px-6 py-3 rounded-xl shadow-lg transition"
                  >
                    <Download className="w-5 h-5" />
                    Exportera till PDF
                  </button>
                </div>
              </div>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
} 